import { animate, motion, useInView, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";

const TARGET = 99.87;

export function Trust() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);

  const sectionRef = useRef<HTMLElement>(null);
  const inView = useInView(sectionRef, { once: true, margin: "-20%" });

  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => v.toFixed(2));

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, TARGET, {
      duration: 2.4,
      ease: [0.22, 1, 0.36, 1],
    });
    return () => controls.stop();
  }, [inView, count]);

  const items = [
    { k: "t1", title: t("trust.i1.title"), desc: t("trust.i1.desc") },
    { k: "t2", title: t("trust.i2.title"), desc: t("trust.i2.desc") },
    { k: "t3", title: t("trust.i3.title"), desc: t("trust.i3.desc") },
    { k: "t4", title: t("trust.i4.title"), desc: t("trust.i4.desc") },
  ];

  return (
    <section ref={sectionRef} className="relative bg-[var(--color-cream)] py-16 sm:py-24 md:py-32 lg:py-40">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 md:px-10 grid grid-cols-12 gap-6 md:gap-8 items-start">
        {/* Left column: title + list */}
        <div className="col-span-12 md:col-span-6">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif text-[32px] sm:text-[40px] md:text-[48px] lg:text-[56px] leading-[1.05] text-[var(--color-ink)]"
          >
            {t("trust.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="mt-4 text-[14px] text-[var(--color-ink)]/55 max-w-[420px]"
          >
            {t("trust.sub")}
          </motion.p>

          <ul className="mt-10 sm:mt-14 md:mt-20 lg:mt-24 border-t border-[var(--color-ink)]/10">
            {items.map((item, i) => {
              const isActive = i === active;
              return (
                <li key={item.k} className="border-b border-[var(--color-ink)]/10">
                  <button
                    type="button"
                    onClick={() => setActive(i)}
                    className="w-full flex items-start gap-8 py-5 text-left group"
                    aria-expanded={isActive}
                  >
                    <span className="text-[13px] text-[var(--color-ink)]/50 w-4 pt-[2px] tabular-nums">
                      {i + 1}
                    </span>
                    <span className="flex-1">
                      <span
                        className={`block text-[15px] md:text-[16px] leading-snug transition-colors duration-300 ${
                          isActive
                            ? "text-[var(--color-ink)]"
                            : "text-[var(--color-ink)]/55 group-hover:text-[var(--color-ink)]/80"
                        }`}
                      >
                        {item.title}
                      </span>
                      <motion.span
                        initial={false}
                        animate={{
                          height: isActive ? "auto" : 0,
                          opacity: isActive ? 1 : 0,
                          marginTop: isActive ? 6 : 0,
                        }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="block overflow-hidden text-[13px] text-[var(--color-ink)]/45 leading-relaxed"
                      >
                        {item.desc}
                      </motion.span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right column: animated cross */}
        <div className="col-span-12 md:col-span-6 flex items-center justify-center">
          <div className="relative w-[240px] h-[240px] sm:w-[300px] sm:h-[300px] md:w-[360px] md:h-[360px] lg:w-[420px] lg:h-[420px] mt-8 md:mt-0">
            <CrossSvg inView={inView} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <motion.span className="font-serif text-[32px] sm:text-[40px] md:text-[48px] lg:text-[56px] text-[var(--color-forest)] tabular-nums leading-none">
                <motion.span>{rounded}</motion.span>%
              </motion.span>
              <span className="mt-2 text-[12px] sm:text-[13px] text-[var(--color-ink)]/55">
                {t("trust.accuracy")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CrossSvg({ inView }: { inView: boolean }) {
  // Greek cross path: 5 squares arrangement. Stroke with pathLength draw-in.
  const d =
    "M 60 0 H 120 V 60 H 180 V 120 H 120 V 180 H 60 V 120 H 0 V 60 H 60 Z";

  return (
    <svg
      viewBox="0 0 180 180"
      className="absolute inset-0 w-full h-full"
      fill="none"
      aria-hidden="true"
    >
      <motion.path
        d={d}
        stroke="var(--color-forest)"
        strokeWidth={1.2}
        strokeLinejoin="miter"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={inView ? { pathLength: 1, opacity: 1 } : {}}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
      />
    </svg>
  );
}
